import React, { useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import LandingScene from '../Components/LandingScene';

const CYAN = '#5FE0FB';
const VIOLET = '#8B5CF6';

const SHELL = 'mx-auto w-full max-w-[1160px] px-5 sm:px-8';

const Arrow = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

const Logo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={CYAN} strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2.5 21 7.5v9L12 21.5 3 16.5v-9z" />
    <path d="M3 7.5l9 5 9-5" />
    <path d="M12 12.5v9" />
  </svg>
);

const stats = [
  { value: '3D', label: 'Interactive learning' },
  { value: 'CBSE', label: 'Curriculum aligned' },
  { value: 'EN + हिं', label: 'Two languages' },
  { value: '360°', label: 'Every environment' },
];

const chapters = [
  {
    index: '01',
    title: 'A lesson is a place',
    body:
      'Students do not watch the lesson. They stand inside it, in a 360° room built for the topic, with 3D models they can walk around and inspect from any angle.',
  },
  {
    index: '02',
    title: 'Four stages, every time',
    body:
      'Intro, Learn, Summary, Quiz. Teachers know the shape of a lesson before they assign it, and students always know where they are inside it.',
  },
  {
    index: '03',
    title: 'Your syllabus, your control',
    body:
      'Chapters and topics follow your curriculum. Every topic carries its own assessment, 3D and imagery, tracks a version, and waits for approval before a class ever sees it.',
  },
  {
    index: '04',
    title: 'Built for a school, not a demo',
    body:
      'Schools, classes, roles and approvals are the product, not an afterthought. Teachers request changes; administrators decide what ships.',
  },
];

const capabilities = [
  ['Immersive 360° environments', 'Every lesson is a room, not a slide deck.'],
  ['Interactive 3D models', 'Examined from any angle, at any distance.'],
  ['Narrated and replayable', 'Audio teaching students can run again.'],
  ['English and Hindi', 'Switch language without rebuilding a lesson.'],
  ['Versioned curriculum', 'Every topic carries a version and a state.'],
  ['Approval before classroom', 'Nothing reaches students unreviewed.'],
];

const footerLinks = [
  { heading: 'Product', links: [['Lessons', '/lessons'], ['Explore', '/explore'], ['Content Studio', '/studio/content'], ['Developers', '/developer']] },
  { heading: 'Schools', links: [['For schools', '/school'], ['For individuals', '/individual'], ['Teacher support', '/teacher-support'], ['Help', '/help']] },
  { heading: 'Company', links: [['Careers', '/careers'], ['Blog', '/blog'], ['Service status', '/service-status']] },
  { heading: 'Legal', links: [['Privacy Policy', '/privacy-policy'], ['Terms & Conditions', '/terms-conditions'], ['Refund Policy', '/refund-policy']] },
];

const Landing = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { subscription, loading: subscriptionLoading, isFreePlan } = useSubscription();

  const scrollerRef = useRef(null);
  const progress = useRef(0); // 0 → 1, read by the WebGL scene each frame

  // The scroller owns the scroll context, so the document must not scroll too.
  useEffect(() => {
    document.documentElement.classList.add('landing-page-active');
    document.body.classList.add('landing-page-active');
    return () => {
      document.documentElement.classList.remove('landing-page-active');
      document.body.classList.remove('landing-page-active');
    };
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      progress.current = max > 0 ? Math.min(1, Math.max(0, el.scrollTop / max)) : 0;
    };

    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const handleGetStarted = () => {
    if (authLoading || subscriptionLoading) {
      return;
    }

    if (user) {
      if (isFreePlan && !subscription) {
        navigate('/onboarding');
      } else {
        navigate('/main');
      }
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="relative h-screen w-full overflow-hidden bg-[#04070f] font-body text-white">
      {/* Fixed backdrop: CSS glow first, WebGL over it. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(900px 620px at 50% 38%, rgba(139,92,246,0.18), transparent 70%), radial-gradient(800px 560px at 82% 78%, rgba(95,224,251,0.12), transparent 72%)',
        }}
      />
      <div className="pointer-events-none absolute inset-0">
        <LandingScene scroll={progress} />
      </div>
      {/* Legibility scrim so type never sits directly on the brightest part of the scene. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'linear-gradient(180deg, rgba(4,7,15,0.55) 0%, rgba(4,7,15,0.15) 28%, rgba(4,7,15,0.82) 72%, #04070f 100%)' }}
      />

      <header className="absolute inset-x-0 top-0 z-40 border-b border-white/[0.08] bg-[#04070f]/60 backdrop-blur-xl">
        <div className={`${SHELL} flex h-[72px] items-center gap-8`}>
          <Link to="/" className="flex items-center gap-2.5 text-white">
            <Logo />
            <span className="font-display text-[19px] font-bold tracking-[-0.01em]">In3D.ai</span>
          </Link>
          <nav className="hidden flex-grow items-center gap-8 lg:flex">
            <Link to="/lessons" className="text-[14px] text-white/65 transition-colors hover:text-white">Lessons</Link>
            <Link to="/explore" className="text-[14px] text-white/65 transition-colors hover:text-white">Explore</Link>
            <Link to="/school" className="text-[14px] text-white/65 transition-colors hover:text-white">For schools</Link>
            <Link to="/help" className="text-[14px] text-white/65 transition-colors hover:text-white">Help</Link>
          </nav>
          <div className="flex flex-grow items-center justify-end gap-4 lg:flex-grow-0">
            <Link to="/login" className="hidden text-[14px] text-white/65 transition-colors hover:text-white sm:block">Sign in</Link>
            <button
              type="button"
              onClick={handleGetStarted}
              className="inline-flex h-10 items-center gap-2 rounded-full px-5 text-[14px] font-semibold text-[#04121b] transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: CYAN }}
            >
              Get started
              <Arrow size={14} />
            </button>
          </div>
        </div>
      </header>

      <div ref={scrollerRef} className="relative z-10 h-screen w-full overflow-y-auto overflow-x-hidden">
        {/* ---------- Hero ---------- */}
        <section className={`${SHELL} flex min-h-screen flex-col items-center justify-center text-center`}>
          <span
            className="inline-flex items-center gap-2.5 rounded-full border px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em]"
            style={{ borderColor: 'rgba(139,92,246,0.4)', backgroundColor: 'rgba(139,92,246,0.1)', color: '#C4B5FD' }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: VIOLET }} />
            CBSE aligned · English &amp; Hindi
          </span>
          <h1 className="mt-8 font-display text-[48px] font-extrabold leading-[0.95] tracking-[-0.04em] sm:text-[78px] lg:text-[104px]">
            Step inside
            <span
              className="block bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(100deg, ${CYAN} 0%, #C4B5FD 52%, ${VIOLET} 100%)` }}
            >
              the lesson
            </span>
          </h1>
          <p className="mx-auto mt-8 max-w-[560px] text-[17px] leading-[1.7] text-white/65 sm:text-[19px]">
            In3D.ai turns a syllabus into rooms students can walk into — 360° environments, 3D models they can
            examine, and a curriculum teachers keep under their own control.
          </p>
          <div className="mt-11 flex flex-col items-stretch justify-center gap-4 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={handleGetStarted}
              className="inline-flex h-[54px] items-center justify-center gap-2.5 rounded-full px-8 text-[16px] font-semibold text-[#04121b] transition-transform hover:-translate-y-0.5"
              style={{ backgroundImage: `linear-gradient(100deg, ${CYAN}, #A78BFA)` }}
            >
              Get started free
              <Arrow size={16} />
            </button>
            <Link
              to="/explore"
              className="inline-flex h-[54px] items-center justify-center rounded-full border border-white/20 px-7 text-[16px] font-medium text-white transition-colors hover:border-white/60"
            >
              Explore lessons
            </Link>
          </div>
          <div className="mt-20 font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">Scroll</div>
        </section>

        {/* ---------- Stats ---------- */}
        <section className={`${SHELL} pt-[40px]`}>
          <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-[20px] border border-white/10 bg-white/10 lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-[#070b16]/80 px-6 py-8 text-center backdrop-blur-sm">
                <dt className="sr-only">{stat.label}</dt>
                <dd>
                  <div className="font-display text-[30px] font-bold tracking-[-0.02em] sm:text-[36px]">{stat.value}</div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{stat.label}</div>
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* ---------- Chapters ---------- */}
        <section className={`${SHELL} pt-[150px]`}>
          <div className="flex flex-col gap-[130px]">
            {chapters.map((chapter) => (
              <article key={chapter.index} className="mx-auto max-w-[760px] text-center">
                <span className="font-mono text-[11px] tracking-[0.3em]" style={{ color: VIOLET }}>{chapter.index}</span>
                <h2 className="mt-6 font-display text-[34px] font-bold leading-[1.08] tracking-[-0.03em] sm:text-[56px]">
                  {chapter.title}
                </h2>
                <p className="mx-auto mt-6 max-w-[620px] text-[17px] leading-[1.75] text-white/65 sm:text-[18px]">
                  {chapter.body}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* ---------- Capabilities ---------- */}
        <section className={`${SHELL} pt-[160px]`}>
          <h2 className="mx-auto max-w-[620px] text-center font-display text-[32px] font-bold leading-[1.1] tracking-[-0.03em] sm:text-[46px]">
            Everything a lesson needs
          </h2>
          <ul className="mx-auto mt-14 grid max-w-[980px] grid-cols-1 gap-px overflow-hidden rounded-[20px] border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map(([title, body]) => (
              <li key={title} className="bg-[#070b16]/85 p-7 backdrop-blur-sm">
                <h3 className="font-display text-[18px] font-semibold">{title}</h3>
                <p className="mt-2.5 text-[14px] leading-[1.65] text-white/60">{body}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* ---------- Closing ---------- */}
        <section className={`${SHELL} pt-[170px] text-center`}>
          <h2 className="mx-auto max-w-[16ch] font-display text-[38px] font-extrabold leading-[1.02] tracking-[-0.035em] sm:text-[68px]">
            Bring your syllabus
            <span
              className="block bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(100deg, ${CYAN}, ${VIOLET})` }}
            >
              into the room
            </span>
          </h2>
          <div className="mt-11 flex flex-col justify-center gap-4 sm:flex-row">
            <button
              type="button"
              onClick={handleGetStarted}
              className="inline-flex h-[54px] items-center justify-center rounded-full px-8 text-[16px] font-semibold text-[#04121b] transition-transform hover:-translate-y-0.5"
              style={{ backgroundImage: `linear-gradient(100deg, ${CYAN}, #A78BFA)` }}
            >
              Get started free
            </button>
            <Link
              to="/school"
              className="inline-flex h-[54px] items-center justify-center rounded-full border border-white/20 px-7 text-[16px] font-medium text-white transition-colors hover:border-white/60"
            >
              Talk to us about your school
            </Link>
          </div>
        </section>

        {/* ---------- Footer ---------- */}
        <footer className="mt-[170px] border-t border-white/10 bg-[#04070f]">
          <div className={`${SHELL} grid grid-cols-2 gap-10 pb-9 pt-16 lg:grid-cols-[1.5fr_repeat(4,1fr)]`}>
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2.5">
                <Logo size={26} />
                <span className="font-display text-[18px] font-bold">In3D.ai</span>
              </div>
              <p className="mt-4 max-w-[280px] text-[14px] leading-[1.65] text-white/55">
                Immersive, curriculum-aligned lessons for schools. Built by Evoneural AI.
              </p>
              <div className="mt-5 inline-flex items-center gap-2.5 text-[13px] text-white/55">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                All systems operational
              </div>
            </div>
            {footerLinks.map((column) => (
              <div key={column.heading}>
                <h3 className="mb-4 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">{column.heading}</h3>
                <div className="flex flex-col gap-2.5 text-[14px]">
                  {column.links.map(([label, to]) => (
                    <Link key={to} to={to} className="text-white/70 transition-colors hover:text-white">{label}</Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className={`${SHELL} border-t border-white/10 pb-12 pt-6 text-[13px] text-white/50`}>
            © 2024 Evoneural AI. All rights reserved.
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Landing;
